import { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { api, formatApiError } from "@/lib/kfmApi";
import DeeperChat from "@/components/DeeperChat";
import CopyButton from "@/components/CopyButton";
import SharePublicDialog from "@/components/SharePublicDialog";
import { buildDeeperText } from "@/lib/studyText";
import { toast } from "sonner";
import { Loader2, ArrowLeft, MessageCircle, Pencil, Check, X, BookOpen, Share2 } from "lucide-react";

// A single auto-saved Go Deeper conversation, opened from History.
export default function DeeperConversation() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [conv, setConv] = useState(null);
  const [error, setError] = useState("");
  const [editing, setEditing] = useState(false);
  const [draftTitle, setDraftTitle] = useState("");
  const [savingTitle, setSavingTitle] = useState(false);
  const [spinning, setSpinning] = useState(false);

  useEffect(() => {
    api
      .get(`/deeper/conversations/${id}`)
      .then(({ data }) => setConv(data))
      .catch((e) => setError(formatApiError(e.response?.data?.detail) || "Couldn't load this conversation"));
  }, [id]);

  const startEdit = () => {
    setDraftTitle(conv.title || "");
    setEditing(true);
  };

  const saveTitle = async () => {
    const title = draftTitle.trim();
    if (!title) { setEditing(false); return; }
    setSavingTitle(true);
    try {
      const { data } = await api.patch(`/deeper/conversations/${id}`, { title });
      setConv((c) => ({ ...c, title: data.title }));
      setEditing(false);
      toast.success("Renamed");
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Couldn't rename this conversation");
    }
    setSavingTitle(false);
  };

  // iOS/Safari requires navigator.clipboard.write(...) to be called SYNCHRONOUSLY inside the
  // click handler (within "transient user activation") — an `await api.get(...)` before the
  // clipboard call (as this used to do) loses that activation and silently fails on phones. The
  // fix: hand the browser a ClipboardItem whose data is a PENDING PROMISE — the write call itself
  // stays synchronous, and the fetch/text-building resolves inside it. Falls back to the old
  // write-after-await approach on browsers without ClipboardItem (desktop-only there, unaffected).
  const copyConversation = () => {
    const textPromise = api.get(`/deeper/conversations/${id}`)
      .then(({ data }) => buildDeeperText(data.chat || [], data.title));
    const onDone = () => toast.success("Conversation copied to clipboard");
    const onFail = () => toast.error("Couldn't copy this conversation");
    if (typeof window.ClipboardItem !== "undefined") {
      const blobPromise = textPromise.then((text) => new Blob([text], { type: "text/plain" }));
      navigator.clipboard.write([new ClipboardItem({ "text/plain": blobPromise })]).then(onDone, () =>
        textPromise.then((text) => navigator.clipboard.writeText(text)).then(onDone, onFail));
    } else {
      textPromise.then((text) => navigator.clipboard.writeText(text)).then(onDone, onFail);
    }
  };

  const turnIntoStudy = async () => {
    setSpinning(true);
    try {
      const { data } = await api.post(`/deeper/conversations/${id}/to-study`);
      toast.success("Building your study…");
      navigate(`/study/${data.id}`);
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Couldn't turn this into a study");
      setSpinning(false);
    }
  };

  if (error) {
    return (
      <div className="px-6 py-16 text-center space-y-4 md:max-w-2xl md:mx-auto">
        <p className="text-slate-400">{error}</p>
        <button onClick={() => navigate("/history")} className="text-[#00E5FF] hover:underline inline-flex items-center gap-1.5">
          <ArrowLeft className="w-4 h-4" /> Back to your journey
        </button>
      </div>
    );
  }

  if (!conv) {
    return <div className="py-20 flex justify-center"><Loader2 className="w-6 h-6 text-[#00E5FF] animate-spin" strokeWidth={1.5} /></div>;
  }

  return (
    <div className="flex flex-col h-[calc(100vh-64px)] md:max-w-2xl md:mx-auto w-full">
      <div className="px-6 pt-8 pb-3 space-y-2 shrink-0">
        <button
          data-testid="deeper-conv-back"
          onClick={() => navigate("/history")}
          className="inline-flex items-center gap-1.5 text-sm text-slate-400 hover:text-[#00E5FF] transition-colors"
        >
          <ArrowLeft className="w-4 h-4" strokeWidth={1.5} /> Your journey
        </button>
        <div className="flex items-center gap-2 text-xs font-medium uppercase tracking-widest text-[#00E5FF]">
          <MessageCircle className="w-4 h-4" strokeWidth={1.5} /> Go Deeper conversation
        </div>

        {editing ? (
          <div className="flex items-center gap-2">
            <input
              data-testid="deeper-title-input"
              autoFocus
              value={draftTitle}
              onChange={(e) => setDraftTitle(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === "Enter") saveTitle();
                if (e.key === "Escape") setEditing(false);
              }}
              maxLength={120}
              className="flex-1 text-2xl text-white bg-transparent border-b border-[#00E5FF]/40 focus:outline-none focus:border-[#00E5FF] py-1"
              placeholder="Name this conversation"
            />
            <button
              data-testid="deeper-title-save"
              onClick={saveTitle}
              disabled={savingTitle}
              className="p-2 rounded-full text-[#00E5FF] hover:bg-[#10222A] disabled:opacity-40"
              title="Save name"
            >
              {savingTitle ? <Loader2 className="w-5 h-5 animate-spin" /> : <Check className="w-5 h-5" strokeWidth={1.75} />}
            </button>
            <button
              data-testid="deeper-title-cancel"
              onClick={() => setEditing(false)}
              className="p-2 rounded-full text-slate-500 hover:bg-[#141B29]"
              title="Cancel"
            >
              <X className="w-5 h-5" strokeWidth={1.75} />
            </button>
          </div>
        ) : (
          <div className="flex items-center gap-2 group">
            <h1 data-testid="deeper-conv-title" className="text-2xl text-white">
              {conv.title || "Open conversation"}
            </h1>
            <button
              data-testid="deeper-title-edit"
              onClick={startEdit}
              className="p-1.5 rounded-full text-slate-500 hover:text-[#00E5FF] hover:bg-[#10222A] transition-colors"
              title="Rename this conversation"
            >
              <Pencil className="w-4 h-4" strokeWidth={1.75} />
            </button>
          </div>
        )}

        <div className="flex flex-wrap items-center gap-2 pt-1">
          <button
            data-testid="deeper-to-study"
            onClick={turnIntoStudy}
            disabled={spinning}
            className="inline-flex items-center gap-1.5 rounded-full border border-[#00E5FF]/30 px-4 py-2 text-sm text-[#00E5FF] hover:bg-[#10222A] transition-colors disabled:opacity-40"
          >
            {spinning ? <Loader2 className="w-4 h-4 animate-spin" /> : <BookOpen className="w-4 h-4" strokeWidth={1.75} />}
            Turn this into a study
          </button>

          <button
            data-testid="deeper-copy"
            onClick={copyConversation}
            className="inline-flex items-center gap-1.5 rounded-full border border-[#29344D] px-4 py-2 text-sm text-slate-400 hover:bg-[#0B0F16] transition-colors"
          >
            Copy conversation
          </button>

          <SharePublicDialog
            basePath={`/deeper/conversations/${id}/share`}
            campaign="deeper_share"
            ctaLabel="See the full conversation:"
            title="Share this conversation"
            description="Create a public link to the whole conversation, then share it anywhere. Turn it off any time."
            hook={conv.title || "A Go Deeper conversation exploring Scripture."}
            previewTitle={conv.title || "A Go Deeper conversation on OpenWord AI"}
            emailSubject="A Go Deeper conversation from OpenWord AI"
            testid="deeper-share"
            showHighlightsToggle
            renderTrigger={(openShare) => (
              <button
                data-testid="deeper-share-open"
                onClick={openShare}
                className="inline-flex items-center gap-1.5 rounded-full bg-[#00E5FF] hover:bg-[#00B8CC] text-[#07090E] px-4 py-2 text-sm font-medium transition-colors"
              >
                <Share2 className="w-4 h-4" strokeWidth={1.75} /> Share
              </button>
            )}
          />
        </div>
      </div>
      <div className="flex-1 min-h-0 border-t border-[#1E273D]">
        <DeeperChat variant="embedded" deeperId={id} initialChat={conv.chat || []} initialHighlights={conv.highlights || []} />
      </div>
    </div>
  );
}
